import { useState } from "react";
import { Icons } from "@admin/components/icons/Icons";
import { Modal } from "@admin/components/ui/Modal";
import { ProductFormModal } from "@admin/components/modals/ProductFormModal";
import { Pagination } from "@admin/components/ui/Pagination";
import { AdminTable } from "@admin/components/shared/AdminTable";
import { useDebounce, useNotifications } from "@admin/hooks";
import { useProducts, useToggleProductStatus, useCreateProduct, useUpdateProduct, useDeleteProduct } from "@admin/hooks";
// import { ProductType } from "@admin/types/product"; // no se usa en dry-run
import type { Product } from "@shared/types/product";
import type { PaginationMeta } from "@shared/types";

export function AdminProductosPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | undefined>(
    undefined
  );
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [productToToggle, setProductToToggle] = useState<Product | undefined>(
    undefined
  );

  const { showSuccess } = useNotifications();

  // Aplicar debounce al searchQuery (500ms de delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Obtener productos de la API
  const {
    data: productsResponse,
    isLoading,
    isError,
    error,
  } = useProducts({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearchQuery || undefined,
  });

  // Mutaciones
  const toggleProductStatusMutation = useToggleProductStatus();
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  const deleteProductMutation = useDeleteProduct();

  // Obtener productos de la respuesta de la API
  const products = productsResponse?.data ?? [];
  const paginationMeta: PaginationMeta | undefined =
    productsResponse?.pagination;
  const totalPages = paginationMeta?.totalPages ?? 1;
  const totalItems = paginationMeta?.total ?? products.length;
  const activePage = paginationMeta?.page ?? currentPage;

  // Función helper para extraer texto plano de HTML y truncar
  const extractPlainText = (html?: string | null): string => {
    if (!html) return "";
    // Remover etiquetas HTML y convertir a texto plano
    const plainText = html
      .replace(/<\/?(p|div|br|span|strong|em|u|ol|ul|li|h[1-6]|blockquote)\b[^>]*>/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, " ")
      .trim();
    return plainText;
  };

  // Función para truncar texto con límite de caracteres
  const truncateText = (text: string, maxLength: number = 100): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Columnas de la tabla
  const columns = [
    {
      header: "NOMBRE",
      accessor: "name" as keyof Product,
    },
    {
      header: "DESCRIPCIÓN",
      accessor: (row: Product) => {
        const plainText = extractPlainText(row.description);
        const truncated = truncateText(plainText, 100);
        return (
          <span className="text-sm text-gray-600 max-w-xs" title={plainText}>
            {plainText ? truncated : "Sin descripción"}
          </span>
        );
      },
    },
    {
      header: "MARCA",
      accessor: "brand" as keyof Product,
    },
    {
      header: "CATEGORÍA",
      accessor: (row: Product) => {
        // categoryId puede ser un string o un objeto con _id y name
        if (typeof row.categoryId === "object" && row.categoryId !== null) {
          return (row.categoryId as { name?: string }).name || "Sin categoría";
        }
        return row.categoryId || "Sin categoría";
      },
    },
    {
      header: "IMÁGENES",
      accessor: (row: Product) => (
        <span className="text-sm text-gray-600">
          {row.images && row.images.length > 0
            ? `${row.images.length} imagen${row.images.length > 1 ? "es" : ""}`
            : "Sin imágenes"}
        </span>
      ),
    },
    {
      header: "ESTADO",
      accessor: (row: Product) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.active
              ? "bg-gray-200 text-gray-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.active ? "Activo" : "Inactivo"}
        </span>
      ),
    },
  ];

  // Acciones para cada fila
  const renderActions = (product: Product) => (
    <div className="flex space-x-2">
      <button
        onClick={() => handleEdit(product)}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        title="Editar producto"
      >
        <Icons.Edit size={16} />
      </button>
      <button
        onClick={() => handleToggleOutstanding(product)}
        className={`p-2 rounded-lg transition-colors ${
          product.outstanding
            ? "text-yellow-600 hover:bg-yellow-50"
            : "text-gray-600 hover:bg-gray-50"
        }`}
        title={product.outstanding ? "Quitar destacado" : "Destacar producto"}
      >
        <Icons.Star size={16} />
      </button>
      <button
        onClick={() => handleToggleStatus(product)}
        className={`p-2 rounded-lg transition-colors ${
          product.active
            ? "text-red-600 hover:bg-red-50"
            : "text-[#282828] hover:bg-gray-100"
        }`}
        title={product.active ? "Desactivar producto" : "Activar producto"}
      >
        {product.active ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
      </button>
      <button
        onClick={() => handleDelete(product)}
        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        title="Eliminar producto"
      >
        <Icons.Trash size={16} />
      </button>
    </div>
  );

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleToggleStatus = (product: Product) => {
    setProductToToggle(product);
    setIsStatusModalOpen(true);
  };

  const handleConfirmToggleStatus = () => {
    if (productToToggle) {
      toggleProductStatusMutation.mutate({ id: productToToggle.id, currentActive: productToToggle.active }, {
        onSuccess: () => {
          const action = productToToggle.active ? "desactivado" : "activado";
          showSuccess("Estado actualizado", `El producto "${productToToggle.name}" ha sido ${action} exitosamente.`, 4000);
        },
        onError: () => {
          showSuccess("Error al cambiar estado", "No se pudo cambiar el estado del producto.", 6000);
        }
      });
      setIsStatusModalOpen(false);
      setProductToToggle(undefined);
    }
  };

  const handleCancelToggleStatus = () => {
    setIsStatusModalOpen(false);
    setProductToToggle(undefined);
  };

  const handleAddProduct = () => {
    setEditingProduct(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(undefined);
  };

  const handleToggleOutstanding = (product: Product) => {
    showSuccess(
      "(Simulación) Acción registrada",
      `Se registró la acción de ${product.outstanding ? "quitar destacado" : "destacar"} para "${product.name}"`,
      3000
    );
  };

  const handleDelete = (product: Product) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar el producto "${product.name}"?`)) {
      deleteProductMutation.mutate(product.id, {
        onSuccess: () => {
          showSuccess("Producto eliminado", `El producto "${product.name}" ha sido eliminado exitosamente.`, 3000);
        },
        onError: () => {
          showSuccess("Error al eliminar", "No se pudo eliminar el producto.", 6000);
        }
      });
    }
  };

  const handleSubmitProduct = (data: {
    name: string;
    categoryId: string;
    description?: string;
    brand?: string;
    unitOfMeasurement?: string;
    outstanding?: boolean;
    order?: number;
    seoTitle?: string;
    seoDescription?: string;
    seoKeywords?: string;
    images?: { url: string; alt?: string; order?: number }[];
  }) => {
    const images = Array.isArray(data.images) ? data.images : [];

    if (editingProduct) {
      const payload = {
        id: editingProduct.id,
        name: data.name,
        categoryId: data.categoryId,
        description: data.description || undefined,
        brand: data.brand || undefined,
        unitOfMeasurement: data.unitOfMeasurement || undefined,
        outstanding: data.outstanding ?? undefined,
        order: data.order ?? undefined,
        seoTitle: data.seoTitle || undefined,
        seoDescription: data.seoDescription || undefined,
        seoKeywords: data.seoKeywords || undefined,
        ...(images.length > 0 ? { images } : {}),
      };
      updateProductMutation.mutate(payload as any, {
        onSuccess: () => {
          showSuccess("Producto actualizado", `El producto "${data.name}" ha sido actualizado exitosamente.`, 3000);
          handleCloseModal();
        },
        onError: () => {
          showSuccess("Error al actualizar", "No se pudo actualizar el producto.", 6000);
        }
      });
    } else {
      const payload = {
        name: data.name,
        categoryId: data.categoryId,
        description: data.description || undefined,
        brand: data.brand || undefined,
        unitOfMeasurement: data.unitOfMeasurement || undefined,
        outstanding: data.outstanding ?? undefined,
        order: data.order ?? undefined,
        seoTitle: data.seoTitle || undefined,
        seoDescription: data.seoDescription || undefined,
        seoKeywords: data.seoKeywords || undefined,
        ...(images.length > 0 ? { images } : {}),
      };
      createProductMutation.mutate(payload as any, {
        onSuccess: () => {
          showSuccess("Producto creado", `El producto "${data.name}" ha sido creado exitosamente.`, 3000);
          handleCloseModal();
        },
        onError: () => {
          showSuccess("Error al crear", "No se pudo crear el producto.", 6000);
        }
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Tabla de productos con funcionalidad extendida */}
      <AdminTable
        title="Gestión de Productos"
        subtitle="Administra el catálogo de productos del sistema"
        searchable={true}
        searchValue={searchQuery}
        onSearchChange={(value) => {
          setSearchQuery(value);
          // Resetear a la página 1 cuando se hace una búsqueda
          setCurrentPage(1);
        }}
        searchPlaceholder="Buscar productos..."
        columns={columns}
        data={products}
        isLoading={isLoading}
        isError={isError}
        error={error?.message}
        renderActions={renderActions}
        actionsHeader="Acciones"
        maxHeight="500px"
        pagination={
          <Pagination
            currentPage={activePage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        }
        onCreateClick={handleAddProduct}
        createButtonText="Agregar Producto"
      />

      {/* Modal de confirmación de cambio de estado */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={handleCancelToggleStatus}
        title={
          productToToggle?.active
            ? "¿Desactivar producto?"
            : "¿Activar producto?"
        }
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {productToToggle?.name} - {productToToggle?.brand} -{" "}
            {productToToggle?.active ? "Activo" : "Inactivo"}
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleCancelToggleStatus}
              className="px-4 py-2 text-[#282828] bg-white border border-[#282828] rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] transition-colors"
              disabled={toggleProductStatusMutation.isPending}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmToggleStatus}
              disabled={toggleProductStatusMutation.isPending}
              className="px-4 py-2 text-white bg-[#282828] border border-transparent rounded-md hover:bg-[#3a3a3a] focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {toggleProductStatusMutation.isPending ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Procesando...
                </span>
              ) : (
                "Confirmar"
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de formulario */}
      {isModalOpen && (
        <ProductFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmitProduct}
          initialData={editingProduct ? {
            name: editingProduct.name,
            categoryId: typeof editingProduct.categoryId === 'string' ? editingProduct.categoryId : (editingProduct.categoryId as { id?: string })?.id || '',
            description: editingProduct.description || '',
            brand: editingProduct.brand || '',
            unitOfMeasurement: editingProduct.unitOfMeasurement || '',
            outstanding: editingProduct.outstanding || false,
            order: editingProduct.order || 0,
            seoTitle: editingProduct.seoTitle || '',
            seoDescription: editingProduct.seoDescription || '',
            seoKeywords: editingProduct.seoKeywords || '',
          } : undefined}
          existingImages={editingProduct?.images || undefined}
          isLoading={createProductMutation.isPending || updateProductMutation.isPending}
        />
      )}
    </div>
  );
}
